import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Building, Mail, DollarSign, Plus, Users } from 'lucide-react';
import { Input, Dropdown, PhoneInput, TextArea } from '../forms';

interface AddCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (companyData: any) => Promise<void>;
  isSubmitting?: boolean;
}

const AddCompanyModal: React.FC<AddCompanyModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [companyData, setCompanyData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    contactPerson: '',
    contactPhone: '',
    contactEmail: '',
    defaultHourlyRate: '',
    companyType: '',
    registrationNumber: '',
    taxNumber: '',
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const companyTypeOptions = [
    { value: 'contractor', label: 'Main Contractor' },
    { value: 'subcontractor', label: 'Sub-contractor' },
    { value: 'supplier', label: 'Supplier' },
    { value: 'consultant', label: 'Consultant' },
    { value: 'vendor', label: 'Vendor' },
    { value: 'other', label: 'Other' },
  ];

  const handleInputChange = (field: string, value: any) => {
    setCompanyData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!companyData.name.trim()) {
      newErrors.name = 'Company name is required';
    }

    if (!companyData.companyType) {
      newErrors.companyType = 'Company type is required';
    }

    if (!companyData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person is required';
    }

    if (companyData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (companyData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid contact email address';
    }

    if (
      companyData.defaultHourlyRate &&
      (isNaN(Number(companyData.defaultHourlyRate)) || Number(companyData.defaultHourlyRate) < 0)
    ) {
      newErrors.defaultHourlyRate = 'Please enter a valid hourly rate';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit({
        ...companyData,
        defaultHourlyRate: companyData.defaultHourlyRate
          ? Number(companyData.defaultHourlyRate)
          : undefined,
      });

      // Reset form
      setCompanyData({
        name: '',
        email: '',
        phone: '',
        address: '',
        contactPerson: '',
        contactPhone: '',
        contactEmail: '',
        defaultHourlyRate: '',
        companyType: '',
        registrationNumber: '',
        taxNumber: '',
        notes: '',
      });
      setErrors({});
    } catch {
      // Error is handled by parent component
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={onClose}
      />

      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[520px] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 p-2 rounded-lg">
                <Plus className="h-5 w-5 text-red-500" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Add New Company</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Information */}
              <div>
                <h4 className="font-medium text-gray-700 flex items-center mb-4">
                  <Building className="h-4 w-4 mr-2" />
                  Company Information
                </h4>

                <div className="space-y-4">
                  <Input
                    label="Company Name"
                    value={companyData.name}
                    onChange={e => handleInputChange('name', e.target.value)}
                    placeholder="Enter company name"
                    error={errors.name}
                    required
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Dropdown
                      label="Company Type"
                      placeholder="Select type"
                      options={companyTypeOptions}
                      value={companyData.companyType}
                      onChange={value => handleInputChange('companyType', value)}
                      error={errors.companyType}
                      required
                    />

                    <Input
                      label="Registration Number"
                      value={companyData.registrationNumber}
                      onChange={e => handleInputChange('registrationNumber', e.target.value)}
                      placeholder="Company registration number"
                    />
                  </div>

                  <Input
                    label="Tax Number"
                    value={companyData.taxNumber}
                    onChange={e => handleInputChange('taxNumber', e.target.value)}
                    placeholder="Tax identification number"
                  />
                </div>
              </div>

              {/* Company Contact */}
              <div>
                <h4 className="font-medium text-gray-700 flex items-center mb-4">
                  <Mail className="h-4 w-4 mr-2" />
                  Company Contact
                </h4>

                <div className="space-y-4">
                  <Input
                    label="Company Email"
                    type="email"
                    value={companyData.email}
                    onChange={e => handleInputChange('email', e.target.value)}
                    placeholder="company@example.com"
                    error={errors.email}
                  />

                  <PhoneInput
                    label="Company Phone"
                    value={companyData.phone}
                    onChange={value => handleInputChange('phone', value)}
                  />

                  <TextArea
                    label="Address"
                    value={companyData.address}
                    onChange={value => handleInputChange('address', value)}
                    placeholder="Company address"
                    rows={3}
                  />
                </div>
              </div>

              {/* Contact Person */}
              <div>
                <h4 className="font-medium text-gray-700 flex items-center mb-4">
                  <Users className="h-4 w-4 mr-2" />
                  Primary Contact Person
                </h4>

                <div className="space-y-4">
                  <Input
                    label="Contact Person Name"
                    value={companyData.contactPerson}
                    onChange={e => handleInputChange('contactPerson', e.target.value)}
                    placeholder="Enter contact person's name"
                    error={errors.contactPerson}
                    required
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Contact Email"
                      type="email"
                      value={companyData.contactEmail}
                      onChange={e => handleInputChange('contactEmail', e.target.value)}
                      placeholder="contact@example.com"
                      error={errors.contactEmail}
                    />

                    <PhoneInput
                      label="Contact Phone"
                      value={companyData.contactPhone}
                      onChange={value => handleInputChange('contactPhone', value)}
                    />
                  </div>
                </div>
              </div>

              {/* Default Rates */}
              <div>
                <h4 className="font-medium text-gray-700 flex items-center mb-4">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Default Rates
                </h4>

                <Input
                  label="Default Hourly Rate (Optional)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={companyData.defaultHourlyRate}
                  onChange={e => handleInputChange('defaultHourlyRate', e.target.value)}
                  placeholder="25.00"
                  error={errors.defaultHourlyRate}
                />
              </div>

              {/* Additional Notes */}
              <div>
                <TextArea
                  label="Notes (Optional)"
                  value={companyData.notes}
                  onChange={value => handleInputChange('notes', value)}
                  placeholder="Additional notes about the company"
                  rows={3}
                />
              </div>
            </form>
          </div>

          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Company
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default AddCompanyModal;
